// @flow
import styled from '@emotion/styled'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, fonts } from '@toggl/style'
import { Responsive, text, useTimeout } from '@toggl/ui'
import { Duration } from 'components/Duration'

import { StartIcon } from './StartIcon'
import { StopIcon } from './StopIcon'

export function TimerCard() {
  const cardContainer = React.useRef<HTMLDivElement | null>(null)
  useTimeout(() => cardContainer.current?.classList.add('active'), 3000)
  return (
    <Responsive.DesktopOnly>
      <CardContainer
        // $FlowFixMe[incompatible-type]: This is a valid ref
        ref={cardContainer}
      >
        <CardWrapper>
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Hover />
          <Card>
            <Timer>
              <TimerDescription>
                <TimerType>Stand-up meeting</TimerType>
              </TimerDescription>
              <LiveTimer />
            </Timer>
            <Layers>
              <Layer />
              <Layer />
              <Layer />
              <Layer />
            </Layers>
          </Card>
          <StaticImage
            src="./toggl-hand.png"
            alt="3D illustration TODO"
            width={120}
            formats={['auto', 'webp', 'avif']}
            placeholder="none"
            objectFit="contain"
            className="interactive-hand"
          />
        </CardWrapper>
        <Quote color={colors.beige}>
          “I love the ease of just clocking in and out with{' '}
          <Rotalic>just one click!</Rotalic>”<small>– Quelani P.</small>
        </Quote>
      </CardContainer>
    </Responsive.DesktopOnly>
  )
}

function LiveTimer() {
  const [since, setSince] = React.useState<number | null>(null)
  useTimeout(() => setSince(Date.now()), 4700)
  return (
    <React.Fragment>
      <TimerTime>
        <text.P2 color={colors.darkPurple}>
          <Duration since={since} />
        </text.P2>
      </TimerTime>
      <TimerButton>
        <StartIcon />
        <TimerStop />
      </TimerButton>
    </React.Fragment>
  )
}

const CardContainer: React.ComponentType<
  React.ElementConfig<'div'>
> = styled.div`
  min-height: 250px;
  perspective: 50em;
  position: relative;
`

const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${colors.pink};
`

const Card = styled.div`
  position: absolute;
  width: 420px;
  padding: 1.5rem;
  color: ${colors.darkPurple};
  transform-style: preserve-3d;
  transition: transform 0.4s;
`

const Quote = styled(text.H4)`
  position: absolute;
  font-size: 22px;
  width: 350px;
  top: 145px;
  left: 40px;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  small {
    display: block;
    text-align: right;
    margin-top: 10px;
    font-family: ${fonts.inter};
  }
  .active & {
    opacity: 1;
    transform: translateY(-30px);
    transition-delay: 3.3s;
  }
`

const Hover = styled.div`
  position: relative;
  z-index: 10;
  transform: translate(-20px, -30px);
  &:nth-of-type(1):hover ~ ${Card} {
    transform: rotateY(-15deg) rotateX(18deg);
  }
  &:nth-of-type(2):hover ~ ${Card} {
    transform: rotateY(-7.5deg) rotateX(18deg);
  }
  &:nth-of-type(3):hover ~ ${Card} {
    transform: rotateY(4deg) rotateX(15deg);
  }
  &:nth-of-type(4):hover ~ ${Card} {
    transform: rotateY(10deg) rotateX(18deg);
  }
  &:nth-of-type(5):hover ~ ${Card} {
    transform: rotateY(15deg) rotateX(18deg);
  }
  &:nth-of-type(6):hover ~ ${Card} {
    transform: rotateY(-15deg) rotateX(-15deg);
  }
  &:nth-of-type(7):hover ~ ${Card} {
    transform: rotateY(-10deg) rotateX(-15deg);
  }
  &:nth-of-type(8):hover ~ ${Card} {
    transform: rotateY(-3deg) rotateX(-15deg);
  }
  &:nth-of-type(9):hover ~ ${Card} {
    transform: rotateY(7.5deg) rotateX(-15deg);
  }
  &:nth-of-type(10):hover ~ ${Card} {
    transform: rotateY(15deg) rotateX(-15deg);
  }
`

const CardWrapper = styled.div`
  top: -20px;
  left: 0;
  position: absolute;
  width: 460px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(2, 70px);

  .interactive-hand {
    position: absolute;
    opacity: 0;
    width: 120px;
    right: 0;
    bottom: -65px;
    transition: 0.4s ease-in;
  }

  .active & .interactive-hand {
    animation: hand-movement 2.8s forwards 0.65s;
  }

  @keyframes hand-movement {
    0%,
    95% {
      opacity: 0;
    }
    25%,
    70% {
      opacity: 1;
      transform: translateY(-35px);
    }
  }
`

const TimerStop = styled(StopIcon)`
  visibility: none;
  opacity: 0;
  transition: 0.1s;

  .active & {
    visibility: visible;
    opacity: 1;
    transition-delay: 2s;
  }
`

const TimerButton = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  box-shadow: 0 0 0 5px ${colors.pink30};
  transition: 0.5s ease;
  svg {
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
  }
  .active & {
    box-shadow: 0 0 0 5px #ffd0ca;
    transition-delay: 2s;
    animation: button-pulse 5s ease 3s infinite;
  }
  @keyframes button-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 5px #ffd0ca;
    }
    50% {
      box-shadow: 0 0 0 0 #ffd0ca;
    }
  }
`

const TimerTime = styled.div`
  margin-left: auto;
`

const TimerType = styled(text.P2)`
  color: ${colors.darkPurple};
  overflow: hidden;
  line-height: 1.2;
  border-right: solid 2px ${colors.darkPurple40};
  white-space: nowrap;
  animation: animated-text 1.75s steps(22, end) 1.5s 1 normal both,
    animated-cursor 600ms steps(22, end) infinite;

  .active & {
    border-right: none;
    transition-delay: 1.5s;
  }

  @keyframes animated-text {
    from {
      width: 0;
    }
    to {
      width: 162px;
    }
  }

  @keyframes animated-cursor {
    from {
      border-right-color: ${colors.darkPurple40};
    }
    to {
      border-right-color: transparent;
    }
  }
`

const TimerDescription = styled.div`
  flex: 1;
`

const Timer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 1rem;
  font-size: 20px;
`

/* for the 3d card effect */
const Layers = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  z-index: -1;
  border-radius: 1em;
  box-shadow: 1px 0 1px -0.5px #f6eddc inset,
    -1.25px 0 0.5px -0.5px #9d9885 inset, 0 0 1px 2px #f7f5e8 inset,
    0 10px 5px -5px rgb(34 33 28 / 45%), 0 10px 7px -3px rgb(40 36 33 / 25%);
`

const Layer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: #fff;
  transform: translateZ(var(--tz));
  box-shadow: 0 0 0.5em #666 inset;
  &:nth-of-type(1) {
    --tz: 0;
  }
  &:nth-of-type(2) {
    --tz: -4px;
  }
  &:nth-of-type(3) {
    --tz: -8px;
  }
  &:nth-of-type(4) {
    --tz: -12px;
  }
  &:last-child {
    box-shadow: 0 0 0.5em #000d inset, 0 0 5px #000;
  }
`
/* end 3d card effect */
